import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  readYesNoDisplayFromAnswer,
  yesNoDisplayToLoincCoding,
} from "@/components/ClearableYesNoRadioField";
import {
  CONTRACTION_STRENGTH_LINK_ID,
  CONTRACTIONS_COPING_LINK_ID,
  CONTRACTIONS_DATE_LINK_ID,
  CONTRACTIONS_DATE_TIME_LINK_ID,
  CONTRACTIONS_DURATION_LINK_ID,
  CONTRACTIONS_FREQUENCY_LINK_ID,
  CONTRACTIONS_NOTES_LINK_ID,
  CONTRACTIONS_ROOT_LINK_ID,
  CONTRACTIONS_SIGNATURE_INITIALS_LINK_ID,
  CONTRACTIONS_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/constants/contractions-link-ids";
import type { ContractionsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/types/contractions-form";
import { getEmptyContractionsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/utils/contractions-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireDecimalAnswer,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";

export function mapQuestionnaireResponseToContractionsFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): ContractionsFlowsheetFormValues {
  // Hydration uses an empty shape so a missing/malformed root never prefills "now".
  const emptyValues = getEmptyContractionsFlowsheetFormValues();
  const root = findQuestionnaireResponseItem(
    questionnaireResponse?.item,
    CONTRACTIONS_ROOT_LINK_ID,
  );
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, CONTRACTIONS_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, CONTRACTIONS_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, CONTRACTIONS_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const copingItem = findQuestionnaireResponseItem(root.item, CONTRACTIONS_COPING_LINK_ID);

  return {
    ...emptyValues,
    contractionDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    contractionTime: timeAnswer ?? null,
    frequency: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, CONTRACTIONS_FREQUENCY_LINK_ID),
    ),
    duration: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, CONTRACTIONS_DURATION_LINK_ID),
    ),
    strength: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, CONTRACTION_STRENGTH_LINK_ID),
    ),
    coping: readYesNoDisplayFromAnswer(copingItem?.answer?.[0]),
    notes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, CONTRACTIONS_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, CONTRACTIONS_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Shape expected by `getQuestionnaireResponseFromFormValues` / labor bots (legacy intake nesting). */
export function mapContractionsFormValuesToQuestionnaireValues(
  formValues: ContractionsFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [CONTRACTIONS_ROOT_LINK_ID]: {
      [CONTRACTIONS_DATE_TIME_LINK_ID]: {
        [CONTRACTIONS_DATE_LINK_ID]: formValues.contractionDate,
        [CONTRACTIONS_TIME_LINK_ID]: formValues.contractionTime,
      },
      [CONTRACTIONS_FREQUENCY_LINK_ID]: formValues.frequency,
      [CONTRACTIONS_DURATION_LINK_ID]: formValues.duration,
      [CONTRACTION_STRENGTH_LINK_ID]: parseCodingFromNativeSelect(formValues.strength),
      [CONTRACTIONS_COPING_LINK_ID]: yesNoDisplayToLoincCoding(formValues.coping),
      [CONTRACTIONS_NOTES_LINK_ID]: formValues.notes,
      [CONTRACTIONS_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
